import { Extension, Node, type Editor } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';
import { FileHandler } from '@tiptap/extension-file-handler';
import { Loader2, Paperclip, X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import type { RichTextEditorLabels, RichTextEditorUpload } from './labels.ts';

/*
 * What a pasted or dropped file may be. The server enforces its own limits;
 * these exist so an oversized or foreign file fails on the spot instead of
 * after a round trip. Callers override them with the constants their backend
 * publishes.
 */
export interface RichTextEditorUploadRules {
  imageTypes: string[];
  fileTypes: string[];
  maxImageBytes: number;
  maxFileBytes: number;
}

export const DEFAULT_UPLOAD_RULES: RichTextEditorUploadRules = {
  imageTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  fileTypes: [
    'application/pdf',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  maxImageBytes: 5 * 1024 * 1024,
  maxFileBytes: 10 * 1024 * 1024,
};

export type UploadFn = (file: File) => Promise<RichTextEditorUpload>;

interface AttachmentContextValue {
  upload: UploadFn;
  rules: RichTextEditorUploadRules;
}

interface AttachmentStorage {
  current: AttachmentContextValue | null;
}

/*
 * The upload handler arrives as a React prop and can change between renders;
 * the editor is built once. Extension storage is Tiptap's home for exactly
 * this: the component writes the latest handler into it, the paste/drop
 * callbacks read it at event time.
 */
export const AttachmentContext = Extension.create<object, AttachmentStorage>({
  name: 'attachmentContext',
  addStorage() {
    return { current: null };
  },
});

const attachmentStorage = (editor: Editor): AttachmentStorage | undefined =>
  (editor.storage as unknown as Record<string, AttachmentStorage | undefined>)[
    AttachmentContext.name
  ];

export const setAttachmentContext = (
  editor: Editor,
  value: AttachmentContextValue | null,
) => {
  const storage = attachmentStorage(editor);
  if (storage) storage.current = value;
};

let uploadCounter = 0;

const nextUploadId = () => {
  uploadCounter += 1;
  return `upload-${uploadCounter}`;
};

interface PlaceholderAttrs {
  id: string;
  name: string;
  kind: 'image' | 'file';
  status: 'uploading' | 'failed' | 'unsupported';
}

const findPlaceholder = (
  editor: Editor,
  id: string,
): { pos: number; size: number } | null => {
  let found: { pos: number; size: number } | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === UploadPlaceholder.name && node.attrs.id === id) {
      found = { pos, size: node.nodeSize };
      return false;
    }
    return true;
  });
  return found;
};

const resolvePlaceholder = (editor: Editor, id: string, result: RichTextEditorUpload) => {
  if (editor.isDestroyed) return;
  const place = findPlaceholder(editor, id);
  if (!place) return;
  const content =
    result.kind === 'image'
      ? { type: 'image', attrs: { src: result.url, alt: result.name } }
      : {
          type: 'paragraph',
          content: [
            { type: FileChip.name, attrs: { href: result.url, name: result.name } },
          ],
        };
  editor
    .chain()
    .insertContentAt({ from: place.pos, to: place.pos + place.size }, content)
    .run();
};

const failPlaceholder = (editor: Editor, id: string) => {
  if (editor.isDestroyed) return;
  const place = findPlaceholder(editor, id);
  if (!place) return;
  const { tr } = editor.state;
  tr.setNodeAttribute(place.pos, 'status', 'failed');
  editor.view.dispatch(tr);
};

/*
 * One file becomes one placeholder block immediately; the upload fills it in
 * or flips it to an error the user can dismiss. A save that fires mid-upload
 * simply does not contain the file yet — the placeholder serializes to
 * nothing, and the finished image lands in the next save.
 */
export const insertFiles = (editor: Editor, files: File[], pos: number) => {
  const context = attachmentStorage(editor)?.current;
  if (!context) return;

  let at = pos;
  for (const file of files) {
    const kind: PlaceholderAttrs['kind'] = context.rules.imageTypes.includes(file.type)
      ? 'image'
      : 'file';
    const supported =
      kind === 'image'
        ? file.size <= context.rules.maxImageBytes
        : context.rules.fileTypes.includes(file.type) &&
          file.size <= context.rules.maxFileBytes;

    const id = nextUploadId();
    const attrs: PlaceholderAttrs = {
      id,
      name: file.name,
      kind,
      status: supported ? 'uploading' : 'unsupported',
    };
    editor.chain().insertContentAt(at, { type: UploadPlaceholder.name, attrs }).run();
    const inserted = findPlaceholder(editor, id);
    if (inserted) at = inserted.pos + inserted.size;

    if (!supported) continue;
    context.upload(file).then(
      (result) => resolvePlaceholder(editor, id, result),
      () => failPlaceholder(editor, id),
    );
  }
};

const placeholderLook = {
  uploading: 'border-line bg-fill-faint text-ink-muted',
  failed: 'border-danger-line bg-danger-fill/50 text-danger',
  unsupported: 'border-danger-line bg-danger-fill/50 text-danger',
} as const;

function UploadPlaceholderView({ node, extension, deleteNode }: NodeViewProps) {
  const { name, status } = node.attrs as PlaceholderAttrs;
  const { labels } = extension.options as { labels: RichTextEditorLabels };
  const message =
    status === 'uploading'
      ? labels.uploading
      : status === 'failed'
        ? labels.uploadFailed
        : labels.uploadUnsupported;

  return (
    <NodeViewWrapper
      data-slot='upload-placeholder'
      className={cn(
        'my-2 flex items-center gap-2.5 rounded-md border px-3 py-2.5 text-sm font-semibold',
        placeholderLook[status],
      )}
    >
      {status === 'uploading' ? (
        <Loader2 className='size-4 shrink-0 animate-spin' aria-hidden='true' />
      ) : (
        <Paperclip className='size-4 shrink-0' aria-hidden='true' />
      )}
      <span className='min-w-0 flex-1 truncate'>
        {message}
        {' — '}
        {name}
      </span>
      {status !== 'uploading' && (
        <button
          type='button'
          aria-label={labels.removeUpload}
          onClick={() => deleteNode()}
          className='shrink-0 rounded-full p-1 transition-colors hover:bg-danger-fill'
        >
          <X className='size-3.5' aria-hidden='true' />
        </button>
      )}
    </NodeViewWrapper>
  );
}

export const UploadPlaceholder = Node.create<{ labels: RichTextEditorLabels }>({
  name: 'uploadPlaceholder',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      id: { default: '' },
      name: { default: '' },
      kind: { default: 'file' },
      status: { default: 'uploading' },
    };
  },

  /* Never persisted: a document only ever contains finished uploads. */
  renderMarkdown: () => '',

  parseHTML() {
    return [];
  },

  renderHTML() {
    return ['div', { 'data-upload-placeholder': '' }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(UploadPlaceholderView);
  },
});

function FileChipView({ node }: NodeViewProps) {
  const { name } = node.attrs as { href: string; name: string };
  return (
    <NodeViewWrapper
      as='span'
      data-slot='file-chip'
      className={cn(
        'mx-0.5 inline-flex max-w-full items-center gap-1.5 rounded-full border border-line',
        'bg-fill-faint px-2.5 py-0.5 align-middle text-sm font-semibold text-ink-secondary',
      )}
    >
      <Paperclip className='size-3.5 shrink-0 text-ink-muted' aria-hidden='true' />
      <span className='truncate'>{name}</span>
    </NodeViewWrapper>
  );
}

const escapeLinkText = (text: string) => text.replace(/([[\]])/g, '\\$1');

/*
 * An uploaded file inside the text. It lives in markdown as a plain link —
 * `[name](url)` — so every reader (the model, the recap, a clipboard) sees a
 * link; the editor upgrades links that point at the app's attachment routes
 * back into chips on load.
 */
export const FileChip = Node.create({
  name: 'fileChip',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      href: { default: '' },
      name: { default: '' },
    };
  },

  renderMarkdown: (node) => {
    const attrs = (node.attrs ?? {}) as { href?: string; name?: string };
    return `[${escapeLinkText(attrs.name ?? '')}](${attrs.href ?? ''})`;
  },

  parseHTML() {
    return [{ tag: 'a[data-file-chip]' }];
  },

  renderHTML({ node }) {
    return [
      'a',
      { 'data-file-chip': '', href: node.attrs.href as string },
      node.attrs.name as string,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileChipView);
  },
});

/*
 * Markdown parses an attachment as an ordinary link; this walks the fresh
 * document and puts the chips back. End-to-start so earlier replacements do
 * not shift later positions.
 */
export const upgradeAttachmentLinks = (
  editor: Editor,
  isAttachmentHref: (href: string) => boolean,
) => {
  const replacements: Array<{ from: number; to: number; href: string; name: string }> =
    [];
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const link = node.marks.find((mark) => mark.type.name === 'link');
    const href = link?.attrs.href as string | undefined;
    if (!link || !href || !isAttachmentHref(href)) return true;
    replacements.push({
      from: pos,
      to: pos + node.nodeSize,
      href,
      name: node.text ?? '',
    });
    return true;
  });
  if (replacements.length === 0) return;

  const { tr } = editor.state;
  for (const { from, to, href, name } of replacements.reverse()) {
    tr.replaceWith(from, to, editor.schema.nodes.fileChip.create({ href, name }));
  }
  tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
};

export const createFileHandler = () =>
  FileHandler.configure({
    onPaste: (editor, files, pasteContent) => {
      /* Copied rich content brings its own HTML; the files are a side effect. */
      if (pasteContent) return;
      insertFiles(editor, files, editor.state.selection.to);
    },
    onDrop: (editor, files, pos) => {
      insertFiles(editor, files, pos);
    },
  });
